import { Icon, IconSVG, SVGColorKey } from "@studio3t/generic-components";
import styled from "styled-components";

export type AccessDeniedKind = "not-entitled" | "retryable";

const COPY: Record<AccessDeniedKind, { title: string; message: string }> = {
  "not-entitled": {
    title: "Access not authorized",
    message:
      "Your account isn't authorized to use 3TL Bridge. Please contact 3T to request access.",
  },
  retryable: {
    title: "Couldn't verify access",
    message:
      "We couldn't verify your access right now. Please try again in a moment — contact us if the problem persists.",
  },
};

/**
 * Shown by the per-user access gate when the user is denied. `not-entitled` is a
 * terminal state (contact 3T); `retryable` offers a retry for transient errors.
 * Both offer sign-out so the user isn't stuck.
 */
export function AccessDeniedPage({
  kind,
  onRetry,
  onSignOut,
}: {
  kind: AccessDeniedKind;
  onRetry?: () => void;
  onSignOut: () => void;
}) {
  const { title, message } = COPY[kind];
  return (
    <Page>
      <Card>
        <Brand>
          <IconSVG
            icon={Icon.Manatee}
            size={36}
            svgColorKey={SVGColorKey.buttonPrimaryIcon}
          />
          <AppName>3TL Bridge</AppName>
        </Brand>

        <Content>
          <Title>{title}</Title>
          <Message>{message}</Message>
        </Content>

        <Actions>
          {kind === "retryable" && onRetry && (
            <Button type="button" onClick={onRetry}>
              Try again
            </Button>
          )}
          <Button type="button" onClick={onSignOut}>
            Sign out
          </Button>
        </Actions>
      </Card>
    </Page>
  );
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: ${(p) => p.theme.semanticAppBackground};
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 28px;
  padding: 48px 40px;
  border-radius: 12px;
  min-width: 320px;
  max-width: 420px;
  background: ${(p) => p.theme.semanticAppBackground};
`;

const Brand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  > div:first-child {
    display: flex;
    justify-content: center;
  }
`;

const AppName = styled.span`
  font-size: 20px;
  font-weight: 600;
  color: ${(p) => p.theme.semanticTextHeading};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: ${(p) => p.theme.semanticTextHeading};
`;

const Message = styled.p`
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
  color: ${(p) => p.theme.semanticTextLight};
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 20px;
  border: 1px solid ${(p) => p.theme.sidebarBorder};
  border-radius: 8px;
  background: transparent;
  color: ${(p) => p.theme.semanticTextBody};
  font-size: 14px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: ${(p) => p.theme.sidebarListItemHoverBackground};
  }
`;
